import React, { useState } from 'react';
import useTheme from '@material-ui/core/styles/useTheme';
import { ThemeProvider } from 'styled-components';
import { Card, Checkbox, Link } from '../ui';
import SettingsIcon from '../../icons/Settings';
import { hasRelation } from '../../helpers/role';
import { AppState } from '../../store/state';
import { selectConference, selectPermission, selectTrackRoles } from '../../store/selectors';
import { AppDispatch } from '../../store/store';
import { Role, RoleDTO, roleRelationDTO, RoleRelationType } from '../../store/conference/types';
import conferenceSlice from '../../store/conference/slice';
import { connect } from 'react-redux';
import RoleDialog from '../dialogs/RoleDialog/RoleDialog';
import { useTranslation } from 'react-i18next';
import { getRoleName } from '../../helpers/translations';
import { PermissionString } from '../../store/permission/types';
import { HowItWorksButton } from '../ui/inputs/HowItWorksButton/HowItWorksButton';
import DocumentationSidePanel from '../ui/utils/DocumentationSidePanel/DocumentationSidePanel';
import BackButton from '../ui/buttons/BackButton/BackButton';

interface Props {
  currentRole: Role;
  roleById: { [key: string]: Role };
  roleRelationsById: { [key in RoleRelationType]: { [key: string]: number[] } };
  updateRoleRelationAction: (data: roleRelationDTO) => void;
  updateRoleAction: (data: RoleDTO) => {};
  onCancel: () => void;
  permissionByRoleId: { [key: number]: PermissionString[] };
}

const RoleManagement: React.FC<Props> = ({
  currentRole,
  roleById,
  roleRelationsById,
  updateRoleRelationAction,
  updateRoleAction,
  onCancel,
  permissionByRoleId,
}) => {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);
  const theme = useTheme();

  const canAccessSummarizedViews = permissionByRoleId[currentRole.id].includes('ACCESS_SUMMARIZED_VIEWS');
  const canEditPaperStatus = permissionByRoleId[currentRole.id].includes('CAN_EDIT_PAPER_STATUS');

  return (
    <ThemeProvider theme={theme}>
      <div className="w-full flex" style={{ flexGrow: 1 }}>
        <div className="text-sm w-full">
          {/* Roles tab page title */}
          <div className="flex h-auto flex-col">
            <div className="pb-4 gap-5 grid grid-cols-1 justify-items-start max-w-5xl">
              <div className="flex mb-2 items-center w-full">
                <BackButton onClick={onCancel} />

                <h2 className="font-bold text-xl ml-8 capitalize">
                  {t('Roles')}: {getRoleName(currentRole, t)}
                </h2>

                <Link
                  onClick={() => {
                    setDialog(
                      <RoleDialog
                        initRole={{
                          ...currentRole,
                          summarized_views: canAccessSummarizedViews,
                          can_edit_paper_status: canEditPaperStatus,
                        }}
                        handleRoleOk={updateRoleAction}
                        roleById={roleById}
                        open={true}
                        handleClose={() => setDialog(undefined)}
                      />,
                    );
                  }}
                  className="cursor-pointer flex items-center ml-8"
                >
                  <SettingsIcon stroke={'#0e4ff0'} />
                  <span className="ml-2 font-medium text-sm">{t('Settings')}</span>
                </Link>

                <HowItWorksButton
                  iconSize="small"
                  src="https://embed.app.guidde.com/playbooks/fjhCWwfQf348aTaykgyZBV"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start mb-12">
            <div>
              <h3 className="font-semibold text-lg mb-2">{t('Role management and relationships')}</h3>
              <p className="text-sm max-w-4xl mb-8">
                {t(
                  `This section provides a high-level view of all roles involved in the review process. Define and manage the relationships between these roles for efficient workflow management.`,
                )}
              </p>
            </div>

            {Object.values(roleById)
              .filter((role) => role.id === currentRole.id)
              .map((fromRole) => (
                <div key={`from-${fromRole.id}`} className="max-w-screen-2xl">
                  <div className="grid grid-cols-6 gap-8 mb-6">
                    {Object.values(roleById)
                      .filter((role) => role.type !== 'chair' && role.type !== currentRole.description)
                      .map((toRole) => (
                        <Card key={`from-${fromRole.id}-to-${toRole.id}`} className="max-w-xs min-w-min">
                          <h2 className="font-bold mb-3">
                            {currentRole.description === toRole.description ? t('Other') + ' ' : ''}
                            {getRoleName(toRole, t)}
                          </h2>
                          <Checkbox
                            label={t('View identity')}
                            checked={hasRelation('identity', fromRole.id, toRole.id, roleRelationsById)}
                            disabled={fromRole.role_id == toRole.id || fromRole.id == toRole.role_id} // Identity permission can't be changed when the role handles/is it handled for/by reviewers
                            onChange={(e) =>
                              updateRoleRelationAction({
                                from_role_id: fromRole.id,
                                to_role_id: toRole.id,
                                type: 'identity',
                                value: e.target.checked,
                              })
                            }
                            className="mb-2"
                          />
                          <Checkbox
                            label={t('Send email')}
                            checked={hasRelation('send-email', fromRole.id, toRole.id, roleRelationsById)}
                            onChange={(e) =>
                              updateRoleRelationAction({
                                from_role_id: fromRole.id,
                                to_role_id: toRole.id,
                                type: 'send-email',
                                value: e.target.checked,
                              })
                            }
                          />
                        </Card>
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </div>

        <DocumentationSidePanel>
          <h3>Lorem ipsum dolor sit amet consectetur in arcu metus.</h3>
          <p>
            Lorem ipsum dolor sit amet consectetur. Risus mi ultrices dolor id tellus mi tellus. Etiam velit mi in hac
            nunc cras venenatis rhoncus. In cras facilisi feugiat sed nibh posuere sollicitudin faucibus sit. Netus amet
            blandit curabitur arcu. Lobortis ridiculus sapien augue quis amet faucibus.{' '}
          </p>
          <p>
            Nibh sapien sit diam aliquam eu adipiscing viverra faucibus at. Placerat congue pellentesque lobortis
            gravida. Ut aliquet sit mattis pharetra pellentesque hendrerit euismod tortor. Natoque ipsum adipiscing
            ultricies volutpat ultrices lectus maecenas malesuada. Pulvinar vel ac et velit. Adipiscing est bibendum
            quis quam.
          </p>
          <p>
            Volutpat ultrices eget eleifend scelerisque donec id id tristique. Et laoreet et enim ut. Volutpat purus
            nunc ipsum nisl bibendum id suspendisse ridiculus cursus.
          </p>
        </DocumentationSidePanel>
      </div>

      {dialog}
    </ThemeProvider>
  );
};

const mapStateToProps = (state: AppState) => ({
  roleById: selectTrackRoles(state),
  roleRelationsById: selectConference(state).roleRelationsById,
  permissionByRoleId: selectPermission(state).byRoleId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateRoleRelationAction: (data: roleRelationDTO) => dispatch(conferenceSlice.actions.UPDATE_ROLE_RELATION(data)),
  updateRoleAction: (data: RoleDTO) => dispatch(conferenceSlice.actions['UPDATE:ROLE'](data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RoleManagement);
